import { NextApiRequest, NextApiResponse } from 'next';
import { createNLPProcessor } from 'data/conversational-search/nlp-processor';
import { HttpStatusCode, HttpRequestMethodTypes } from 'types';
import config from 'config.json';
import { SearchParameters } from 'data/conversational-search/types';
import { sub250Chars } from 'utils/stringSanitization';
import { containsCompetitorName } from 'utils/stringSanitization';
import { createAIProvider, getProviderApiKey, AIProviderType } from 'data/conversational-search/providers';

export class CompetitorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CompetitorError';
  }
}

// Response types for better error handling and observability
interface ChatSuccessResponse {
  success: true;
  searchParams: SearchParameters;
  message: string;
  needsClarification: boolean;
  clarification?: string;
  followUpSuggestions?: string[];
  isReadyToSearch: boolean;
}

interface ChatErrorResponse {
  success: false;
  error: string;
  errorCode: 'UNAUTHORIZED' | 'UNPROCESSABLE_ENTITY' | 'INTERNAL_SERVER_ERROR' | 'BAD_REQUEST';
  message: string;
  details?: Record<string, unknown>;
}

interface WelcomeMessageSuccessResponse {
  success: true;
  hasLastSearch: boolean;
  message: string;
  searchParams: SearchParameters;
}

// Helper function to generate personalized welcome message using AI provider
async function generateWelcomeMessage(userName: string, searchParams: SearchParameters): Promise<{ success: true; message: string } | { success: false; error: string; errorCode: 'UNPROCESSABLE_ENTITY' | 'INTERNAL_SERVER_ERROR' }> {
  const providerType = config.ai.provider as AIProviderType;
  const apiKey = getProviderApiKey(providerType);
  
  if (!apiKey) {
    console.warn(`AI API key not found for provider: ${providerType}`);
    return { success: false, error: 'AI API key not configured', errorCode: 'UNPROCESSABLE_ENTITY' };
  }

  if (!searchParams || Object.keys(searchParams).length === 0) {
    return { success: true, message: `Hello ${userName}! I can help you find properties. Try saying something like "Show me 3 bedroom houses in Miami under $800k"` };
  }

  try {
    const provider = createAIProvider(providerType, apiKey, {
      model: config.ai.model,
      temperature: config.ai.temperature,
      maxTokens: config.ai.maxTokens,
    });
    const system = `
You are a warm, friendly real estate assistant. Create ONE personalized, welcoming two-sentence message for a returning user.

STYLE & STRUCTURE (FRIENDLY VARIATIONS):
- Sentence 1: Choose from these friendly greetings (use the user's name naturally):
  * "Hi ${userName}! Great to see you back. How's your search for {subject} coming along?"
  * "Welcome back, ${userName}! I hope you're finding what you're looking for with your {subject} search."
  * "Hey ${userName}! Ready to continue your hunt for {subject}?"
  * "Hi ${userName}! How's your search for {subject} going? I'm here to help!"
  * "Welcome back, ${userName}! Let's pick up where we left off with your {subject} search."

- Subject assembly (use only fields present in JSON, in this order):
  1) bedrooms -> "{N} bedroom" or "{N+} bedroom" (use the value as-is; keep "+"; SKIP if value is "0" or "0+")
  2) bathrooms -> "{N} bathroom" or "{N+} bathroom" (use the value as-is; keep "+"; SKIP if value is "0" or "0+")
  3) property type(s) -> if ALL types are true (house, condo, townhouse, farm, land, commercial), use "homes"; if multiple but not all, join with "and" ("houses and condos"); if single type, use that specific type ("house", "condo", "townhouse", "farm", "land", "commercial"); if no type, use "homes".
  4) location -> "in {city[, PROV/STATE]}" (prefer city; append province/state code if provided)
  Final subject pattern: "{beds} {baths} {types} in {location}" with missing parts omitted gracefully.

- Sentence 2: Choose from these friendly follow-ups:
  * "I've refreshed your search{details} - feel free to adjust anything you'd like!"
  * "I've updated your search{details} - what would you like to explore today?"
  * "Your search{details} is ready to go - let me know if you want to try something different!"
  * "I've got your search{details} all set up - ready to find your perfect place?"
  * "Your search{details} is back up and running - anything you'd like to change?"

DETAILS PARENTHESIS (COMPACT, ORDERED, ONLY IF FIELDS EXIST):
- Start details with a space and parenthesis after "search": " ( ... )"
- Include items in this order, comma-separated, only if present:
  1) price: both -> "$600k–$900k"; only max -> "up to $800k"; only min -> "from $600k"
  2) parkingSpaces -> "{N}{+} parking" (e.g., "2+ parking")
  3) size -> "1,200+ sqft" (sqftMin), "under 1,500 sqft" (sqftMax), or "1,200–1,800 sqft" (both); use thousands separators
  4) features -> prefixed with "with ", list any of [garage, pool, fireplace, waterfront] that are true; join with " and " if two, else commas with ", and" before the last for 3+
  5) rental -> ONLY include "for rent" if rental is true; DO NOT include "to buy" or "for sale" unless specifically relevant
- If no details exist, omit the parentheses completely.

CONSTRAINTS:
- Do NOT invent or infer fields beyond the JSON. If a field isn't present, omit it.
- Keep under 300 characters total.
- Use plain ASCII (no curly quotes). No line breaks.
- Return ONLY the final two-sentence string.
- Vary your greeting style - don't use the same pattern every time.
`;

    // Format search parameters for AI: bedrooms, bathrooms, city, homeType, priceMin/Max, etc.
    const userMessage = `searchParams JSON:\n${JSON.stringify(searchParams, null, 2)}`;

    const resp = await provider.chat([
      { role: 'system', content: system },
      { role: 'user', content: userMessage },
    ]);

    const text = resp.content?.trim();
    if (!text || /\n/.test(text) || text.length > 300) {
      return { success: false, error: 'Invalid response from AI provider', errorCode: 'UNPROCESSABLE_ENTITY' };
    }
    return { success: true, message: text };
  } catch (error) {
    console.error('AI API error:', error);
    return { success: false, error: 'AI API request failed', errorCode: 'INTERNAL_SERVER_ERROR' };
  }
}

export default async function handler(req: NextApiRequest, res: NextApiResponse) {
  if (req.method !== HttpRequestMethodTypes.POST) {
    return res.status(HttpStatusCode.METHOD_NOT_ALLOWED).json({ error: 'Method not allowed' });
  }

  try {
    const { query, currentParams, aiSearchParams, recentMessages } = req.body;
    
    // Handle welcome message request (when aiSearchParams is provided)
    if (aiSearchParams !== undefined) {
      const userName = req.query.userName as string || 'there';
      const searchParams = aiSearchParams || {};
      
      const welcomeMessageResult = await generateWelcomeMessage(userName, searchParams);
      
      if (welcomeMessageResult.success) {
        const successResponse: WelcomeMessageSuccessResponse = {
          success: true,
          hasLastSearch: Object.keys(searchParams).length > 0,
          message: welcomeMessageResult.message,
          searchParams: searchParams,
        };
        return res.status(HttpStatusCode.OK).json(successResponse);
      } else {
        const errorResult = welcomeMessageResult as { success: false; error: string; errorCode: 'UNPROCESSABLE_ENTITY' | 'INTERNAL_SERVER_ERROR' };
        const errorResponse: ChatErrorResponse = {
          success: false,
          error: errorResult.error,
          errorCode: errorResult.errorCode,
          message: 'Failed to generate welcome message',
        };
        return res.status(HttpStatusCode.UNPROCESSABLE_ENTITY).json(errorResponse);
      }
    }
    
    // Handle regular chat query
    if (!query) {
      const errorResponse: ChatErrorResponse = {
        success: false,
        error: 'Query is required',
        errorCode: 'BAD_REQUEST',
        message: 'Please provide a search query',
      };
      return res.status(HttpStatusCode.BAD_REQUEST).json(errorResponse);
    }

    // Check if AI API key is available
    const providerType = config.ai.provider as AIProviderType;
    const apiKey = getProviderApiKey(providerType);
    if (!apiKey) {
      const errorResponse: ChatErrorResponse = {
        success: false,
        error: 'AI API key not configured',
        errorCode: 'UNPROCESSABLE_ENTITY',
        message: 'Chat service is temporarily unavailable',
      };
      return res.status(HttpStatusCode.UNPROCESSABLE_ENTITY).json(errorResponse);
    }
    if (containsCompetitorName(query)) {
      throw new CompetitorError('Query contains competitor name');
    }

    try {
      // Create AI provider and initialize the NLP processor
      const provider = createAIProvider(providerType, apiKey, {
        model: config.ai.model,
        temperature: config.ai.temperature,
        maxTokens: config.ai.maxTokens,
      });
      const nlpProcessor = createNLPProcessor(provider);

      // Format recent messages (already limited to 5 by frontend)
      // Ensure messages are in the correct format
      const formattedRecentMessages: Array<{ role: 'user' | 'assistant'; content: string }> | undefined =
        recentMessages && Array.isArray(recentMessages)
          ? recentMessages
            .map((msg: { role?: string; content?: string }) => ({
              role: (msg.role === 'assistant' ? 'assistant' : 'user') as 'user' | 'assistant',
              content: msg.content || '',
            }))
            .filter((msg: { role: 'user' | 'assistant'; content: string }) => msg.content.trim().length > 0)
          : undefined;

      // Process the query to extract search parameters
      const nlpResponse = await nlpProcessor.processQuery(sub250Chars(query), currentParams || {}, formattedRecentMessages);
      // Prioritize message if present (e.g., help messages), then clarification, then fallback
      const message = nlpResponse.message 
        ? nlpResponse.message
        : nlpResponse.needsClarification 
          ? (nlpResponse.clarification || 'I need more information to help you find properties.')
          : nlpResponse.isReadyToSearch
            ? 'Let me search for properties that match your criteria.'
            : 'Sounds good. Let me know if you need to add more details.';


      const successResponse: ChatSuccessResponse = {
        success: true,
        searchParams: nlpResponse.searchParams,
        message,
        needsClarification: nlpResponse.needsClarification,
        clarification: nlpResponse.clarification,
        followUpSuggestions: nlpResponse.followUpSuggestions,
        isReadyToSearch: nlpResponse.isReadyToSearch,
      };

      return res.status(HttpStatusCode.OK).json(successResponse);
    } catch (error) {
      console.error('NLP processing error:', error);
      const errorResponse: ChatErrorResponse = {
        success: false,
        error: 'Failed to process query',
        errorCode: 'INTERNAL_SERVER_ERROR',
        message: 'I encountered an error processing your request. Please try again.',
        details: { query: query.substring(0, 100) }, // Log first 100 chars for debugging
      };
      return res.status(HttpStatusCode.INTERNAL_SERVER_ERROR).json(errorResponse);
    }

  } catch (error) {
    console.error('Error in conversational-search API:', error);
    if (error instanceof CompetitorError) {
      return res.status(HttpStatusCode.FORBIDDEN).json({
        error: 'Query contains competitor name',
        message: 'This chat is designed for use on eXp Realty websites only. Please enter information about your home search.',
      });
    }
    const errorResponse: ChatErrorResponse = {
      success: false,
      error: 'Internal server error',
      errorCode: 'INTERNAL_SERVER_ERROR',
      message: 'I encountered an error processing your request. Please try again.',
      details: { error: error instanceof Error ? error.message : 'Unknown error' },
    };
    return res.status(HttpStatusCode.INTERNAL_SERVER_ERROR).json(errorResponse);
  }
} 